import React, { useState } from 'react';
import api from '../services/api';
import { Search, Plus, X, AlertTriangle, Shield, Truck, Activity } from 'lucide-react';

interface CarrierSnapshot {
  dotNumber: number;
  legalName: string;
  state: string;
  powerUnits: number;
  drivers: number;
  safetyRating: string;
  operatingStatus: string;
  smsScores: Record<string, number | null>;
  oosOrders: { active: number; total: number };
  crashCount: number;
  fatalCrashes: number;
  violationCount: number;
  inspectionCount: number;
  oosRate: number;
  insurerCount: number;
  lapseCount: number;
  riskScore: number | null;
  revocationProbability: number | null;
  authorityAge: number | null;
  relatedCarriers: number;
}

const CarrierComparePage: React.FC = () => {
  const [dotInputs, setDotInputs] = useState<string[]>(['', '']);
  const [carriers, setCarriers] = useState<CarrierSnapshot[]>([]);
  const [highlights, setHighlights] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const addInput = () => { if (dotInputs.length < 5) setDotInputs([...dotInputs, '']); };
  const removeInput = (i: number) => { if (dotInputs.length > 2) setDotInputs(dotInputs.filter((_, idx) => idx !== i)); };
  const updateInput = (i: number, v: string) => { const n = [...dotInputs]; n[i] = v; setDotInputs(n); };

  const compare = async () => {
    const dots = dotInputs.filter(d => d.trim()).join(',');
    if (dots.split(',').length < 2) { setError('Enter at least 2 DOT numbers'); return; }
    setLoading(true); setError('');
    try {
      const res = await api.get(`/carriers/compare?dots=${dots}`);
      setCarriers(res.data.data.carriers);
      setHighlights(res.data.data.highlights);
    } catch (e: any) { setError(e.response?.data?.error || 'Comparison failed'); }
    setLoading(false);
  };

  const ratingColor = (rating: string) => {
    if (rating === 'S') return '#16a34a';
    if (rating === 'C') return '#ca8a04';
    if (rating === 'U') return '#dc2626';
    return '#6b7280';
  };

  const metricCell = (value: number, best: number, worst: number, lowerIsBetter = true) => {
    const isBest = lowerIsBetter ? value <= best : value >= best;
    const isWorst = lowerIsBetter ? value >= worst : value <= worst;
    return {
      fontWeight: isBest || isWorst ? 'bold' as const : 'normal' as const,
      color: isBest ? '#16a34a' : isWorst ? '#dc2626' : '#111827'
    };
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
      <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
        <Shield size={24} style={{ display: 'inline', marginRight: '8px' }} />
        Carrier Comparison
      </h1>
      <p style={{ color: '#6b7280', marginBottom: '24px' }}>Compare up to 5 carriers side-by-side across safety, insurance, and risk metrics.</p>

      {/* Input Section */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '24px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          {dotInputs.map((v, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <input
                type="text" value={v} onChange={e => updateInput(i, e.target.value)}
                placeholder={`DOT #${i + 1}`}
                style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', width: '140px', fontSize: '14px' }}
                onKeyDown={e => e.key === 'Enter' && compare()}
              />
              {dotInputs.length > 2 && (
                <button onClick={() => removeInput(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
                  <X size={16} />
                </button>
              )}
            </div>
          ))}
          {dotInputs.length < 5 && (
            <button onClick={addInput} style={{ padding: '8px', border: '1px dashed #d1d5db', borderRadius: '8px', background: 'none', cursor: 'pointer', color: '#6b7280' }}>
              <Plus size={16} />
            </button>
          )}
          <button onClick={compare} disabled={loading}
            style={{ padding: '8px 20px', background: '#1e40af', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, opacity: loading ? 0.6 : 1 }}>
            {loading ? 'Comparing...' : 'Compare'}
          </button>
        </div>
        {error && <div style={{ color: '#dc2626', marginTop: '8px', fontSize: '14px' }}>{error}</div>}
      </div>

      {/* Highlights */}
      {highlights.length > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
            <AlertTriangle size={16} style={{ display: 'inline', marginRight: '6px' }} />
            Key Findings
          </h3>
          {highlights.map((h, i) => <div key={i} style={{ fontSize: '14px', marginBottom: '4px' }}>{h}</div>)}
        </div>
      )}

      {/* Comparison Table */}
      {carriers.length > 0 && (
        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #e5e7eb', minWidth: '160px' }}>Metric</th>
                {carriers.map(c => (
                  <th key={c.dotNumber} style={{ padding: '12px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #e5e7eb', minWidth: '150px' }}>
                    <div style={{ fontSize: '13px' }}>{c.legalName}</div>
                    <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: 400 }}>DOT {c.dotNumber}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* General */}
              <tr style={{ background: '#f0f9ff' }}><td colSpan={carriers.length + 1} style={{ padding: '8px 12px', fontWeight: 600, fontSize: '13px' }}><Truck size={14} style={{ display: 'inline', marginRight: '6px' }} />General</td></tr>
              {[
                { label: 'State', get: (c: CarrierSnapshot) => c.state },
                { label: 'Fleet Size', get: (c: CarrierSnapshot) => c.powerUnits.toLocaleString() },
                { label: 'Drivers', get: (c: CarrierSnapshot) => c.drivers.toLocaleString() },
                { label: 'Safety Rating', get: (c: CarrierSnapshot) => c.safetyRating, color: (c: CarrierSnapshot) => ratingColor(c.safetyRating) },
                { label: 'Authority Age', get: (c: CarrierSnapshot) => c.authorityAge ? `${c.authorityAge} yr` : 'N/A' },
              ].map((row, i) => (
                <tr key={i}>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6', color: '#6b7280' }}>{row.label}</td>
                  {carriers.map(c => (
                    <td key={c.dotNumber} style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', color: row.color ? row.color(c) : undefined, fontWeight: row.color ? 'bold' : undefined }}>
                      {row.get(c)}
                    </td>
                  ))}
                </tr>
              ))}

              {/* Safety */}
              <tr style={{ background: '#fef2f2' }}><td colSpan={carriers.length + 1} style={{ padding: '8px 12px', fontWeight: 600, fontSize: '13px' }}><AlertTriangle size={14} style={{ display: 'inline', marginRight: '6px' }} />Safety</td></tr>
              {[
                { label: 'OOS Rate', get: (c: CarrierSnapshot) => `${c.oosRate}%`, val: (c: CarrierSnapshot) => c.oosRate, lower: true },
                { label: 'Active OOS Orders', get: (c: CarrierSnapshot) => c.oosOrders.active > 0 ? `⚠️ ${c.oosOrders.active}` : '0', val: (c: CarrierSnapshot) => c.oosOrders.active, lower: true },
                { label: 'Crashes', get: (c: CarrierSnapshot) => c.crashCount.toString(), val: (c: CarrierSnapshot) => c.crashCount, lower: true },
                { label: 'Fatal Crashes', get: (c: CarrierSnapshot) => c.fatalCrashes > 0 ? `💀 ${c.fatalCrashes}` : '0', val: (c: CarrierSnapshot) => c.fatalCrashes, lower: true },
                { label: 'Violations', get: (c: CarrierSnapshot) => c.violationCount.toLocaleString(), val: (c: CarrierSnapshot) => c.violationCount, lower: true },
                { label: 'Inspections', get: (c: CarrierSnapshot) => c.inspectionCount.toLocaleString(), val: (c: CarrierSnapshot) => c.inspectionCount, lower: false },
              ].map((row, i) => {
                const vals = carriers.map(c => row.val(c));
                const best = row.lower ? Math.min(...vals) : Math.max(...vals);
                const worst = row.lower ? Math.max(...vals) : Math.min(...vals);
                return (
                  <tr key={i}>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6', color: '#6b7280' }}>{row.label}</td>
                    {carriers.map(c => (
                      <td key={c.dotNumber} style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', ...metricCell(row.val(c), best, worst, row.lower) }}>
                        {row.get(c)}
                      </td>
                    ))}
                  </tr>
                );
              })}

              {/* SMS Scores */}
              <tr style={{ background: '#f0fdf4' }}><td colSpan={carriers.length + 1} style={{ padding: '8px 12px', fontWeight: 600, fontSize: '13px' }}><Activity size={14} style={{ display: 'inline', marginRight: '6px' }} />SMS BASIC Percentiles</td></tr>
              {['unsafeDriving', 'hosFatigue', 'vehicleMaint', 'controlledSubstances', 'driverFitness'].map((basic, i) => {
                const label = basic.replace(/([A-Z])/g, ' $1').trim();
                const vals = carriers.map(c => c.smsScores[basic] || 0);
                const best = Math.min(...vals);
                const worst = Math.max(...vals);
                return (
                  <tr key={i}>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6', color: '#6b7280' }}>{label}</td>
                    {carriers.map(c => {
                      const v = c.smsScores[basic];
                      return (
                        <td key={c.dotNumber} style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', ...(v !== null ? metricCell(v, best, worst, true) : { color: '#9ca3af' }) }}>
                          {v !== null ? `${v}%` : 'N/A'}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}

              {/* Insurance */}
              <tr style={{ background: '#eff6ff' }}><td colSpan={carriers.length + 1} style={{ padding: '8px 12px', fontWeight: 600, fontSize: '13px' }}><Shield size={14} style={{ display: 'inline', marginRight: '6px' }} />Insurance & Fraud</td></tr>
              {[
                { label: 'Unique Insurers', get: (c: CarrierSnapshot) => c.insurerCount.toString() },
                { label: 'Cancellations', get: (c: CarrierSnapshot) => c.lapseCount > 3 ? `⚠️ ${c.lapseCount}` : c.lapseCount.toString() },
                { label: 'Related Carriers', get: (c: CarrierSnapshot) => c.relatedCarriers > 0 ? `🔗 ${c.relatedCarriers}` : '0' },
              ].map((row, i) => (
                <tr key={i}>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6', color: '#6b7280' }}>{row.label}</td>
                  {carriers.map(c => (
                    <td key={c.dotNumber} style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>{row.get(c)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CarrierComparePage;
